import { useState, useEffect, memo, Fragment } from 'react';
import getConfig from 'next/config';

import { ProductGrid } from '../product-grid/ProductGrid';
import AppliedFilters from '../facets/applied-filters/AppliedFilters';
import Loading from '../loading/Loading';
import Pagination from '../pagination/Pagination';
import PrimaryFacet from '../facets/primary-facet/PrimaryFacet';
import StickyControls from '../sticky-controls/StickyControls';
import { FoundIt } from '../foundit/FoundIt';
import styles from './ProductListing.module.scss';
import { useProductListingData } from './data/useProductListingData';
import PlpViewTypeSelector from '../plp-view-type-selector/PlpViewTypeSelector';
import PlpCardSizeSelector from '../plp-card-size-selector/PlpCardSizeSelector';

type ProductListingProps = {
  handleDisplayM2MProductSelector: () => void;
  handleDisplayQuickAdd: () => void;
  handleFilterDrawerClick: () => void;
  handlePrimaryFacetClick: () => void;
  handleQuickViewClick: () => void;
};

export const getCategory = (breadcrumbs) => {
  if (breadcrumbs && breadcrumbs[breadcrumbs.length - 1]) {
    return breadcrumbs[breadcrumbs.length - 1]?.displayName || '';
  }
  return '';
};

const ProductListing = memo(
  ({
    handleDisplayM2MProductSelector,
    handleDisplayQuickAdd,
    handleFilterDrawerClick,
    handlePrimaryFacetClick,
    handleQuickViewClick = () => null,
  }: ProductListingProps) => {
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [isLoadingChunk, setIsLoadingChunk] = useState(false);
    const [isChunkLoadError, setIsChunkLoadError] = useState(false);

    const {
      anchorCard,
      experiencesContext,
      facetContext,
      pagesAvailable,
      isLargeCardSize,
      isListView,
      hasPlpViewTypeSelection,
      router,
    } = useProductListingData();

    const {
      isFoundItFlagOn,
      isProductCardSizeFlagOn,
      isCuratedCategoriesFlagOn,
    } = experiencesContext;

    const { publicRuntimeConfig: features } = getConfig();

    const { baseFacetId, isPrimaryFacetInDiscoResponse, primaryFacet } =
      facetContext;

    const { sortBy, page } = router.query;
    const pageAsNumber = parseInt(page as string, 10) || 1;

    useEffect(() => {
      const { events } = router;

      const handleRouteChangeStart = (url: string, { shallow }) => {
        if (!shallow) {
          setIsLoadingPage(true);
        }
      };
      const handleRouteChangeComplete = (url: string, { shallow }) => {
        if (!shallow) {
          setIsLoadingPage(false);
        }
      };
      const handleRouteChangeError = (err, url: string, { shallow }) => {
        if (!shallow) {
          setIsLoadingPage(false);
        }
        console.error(
          `Error ${shallow ? 'shallow' : ''} navigating to ${url}`,
          err
        );
      };

      events.on('routeChangeStart', handleRouteChangeStart);
      events.on('routeChangeComplete', handleRouteChangeComplete);
      events.on('routeChangeError', handleRouteChangeError);

      return () => {
        events.off('routeChangeStart', handleRouteChangeStart);
        events.off('routeChangeComplete', handleRouteChangeComplete);
        events.off('routeChangeError', handleRouteChangeError);
      };
    });

    useEffect(
      () => {
        if (anchorCard) {
          document
            .querySelector(
              `[data-testid="product-card"][data-product-id="${anchorCard}"]`
            )
            .scrollIntoView();
          // scroll a bit more because card will be obscured by sticky controls and sticky search bar
          scrollBy(0, -165);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isLargeCardSize, isListView]
    );

    return (
      <Fragment>
        {isLoadingPage && <Loading showSpinner={false} />}

        <AppliedFilters
          baseFacetId={baseFacetId}
          className={'mobile-facets'}
          grid
          mobile
          numberOfFiltersToShowClear={features.numberOfFiltersToShowClear}
        />

        {hasPlpViewTypeSelection && (
          <PlpViewTypeSelector className="aboveControls" name="aboveControls" />
        )}

        {isPrimaryFacetInDiscoResponse ? (
          <PrimaryFacet
            className="mobile"
            handlePrimaryFacetClick={handlePrimaryFacetClick}
            primaryFacetDisplayName={primaryFacet.displayName}
          />
        ) : null}

        {isFoundItFlagOn && !isCuratedCategoriesFlagOn && <FoundIt />}

        {!isCuratedCategoriesFlagOn && (
          <StickyControls
            current={pageAsNumber}
            handleFilterDrawerClick={handleFilterDrawerClick}
            handlePrimaryFacetClick={
              isPrimaryFacetInDiscoResponse
                ? handlePrimaryFacetClick
                : undefined
            }
            pages={pagesAvailable}
            primaryFacetDisplayName={
              isPrimaryFacetInDiscoResponse
                ? primaryFacet.displayName
                : undefined
            }
            sortBy={sortBy}
          />
        )}

        {isProductCardSizeFlagOn && (
          <PlpCardSizeSelector className="belowControls" name="belowControls" />
        )}

        <div
          id="main-content"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          aria-label="Product listing page results"
          className={styles.mainContent}
        >
          <ProductGrid
            handleQuickViewClick={handleQuickViewClick}
            handleDisplayM2MProductSelector={handleDisplayM2MProductSelector}
            handleDisplayQuickAdd={handleDisplayQuickAdd}
            isChunkLoadError={isChunkLoadError}
            isLoadingChunk={isLoadingChunk}
            isLoadingPage={isLoadingPage}
            setIsChunkLoadError={setIsChunkLoadError}
            setIsLoadingChunk={setIsLoadingChunk}
          />
        </div>

        {!isLoadingChunk && !isChunkLoadError && (
          <Pagination
            className="c-pagination--bottom"
            current={pageAsNumber}
            isFooter
            pages={pagesAvailable}
          />
        )}
      </Fragment>
    );
  }
);

ProductListing.displayName = 'ProductListing';

export default ProductListing;
